/**
 * Supported languages. Hungarian is the source language; every other locale
 * file must contain exactly the same keys (checked by test/completeness.test.ts).
 *
 * To add a language: copy locales/hu.json to locales/<code>.json, translate it,
 * add an entry here and add docs/src/manual_<code>.md. Nothing else changes.
 */
export type TranslationStatus = "source" | "reviewed" | "machine";

export interface LanguageInfo {
  /** BCP 47 code, also the locale file name. */
  code: string;
  /** Name of the language in the language itself, shown in the language switcher. */
  nativeName: string;
  /** English name, for documentation. */
  englishName: string;
  status: TranslationStatus;
}

export const SOURCE_LANGUAGE = "hu";
export const FALLBACK_FOR_UNKNOWN = "en";

export const LANGUAGES: readonly LanguageInfo[] = [
  { code: "hu", nativeName: "Magyar", englishName: "Hungarian", status: "source" },
  { code: "en", nativeName: "English", englishName: "English", status: "reviewed" },
  { code: "de", nativeName: "Deutsch", englishName: "German", status: "reviewed" },
  { code: "sk", nativeName: "Slovenčina", englishName: "Slovak", status: "machine" },
  { code: "cs", nativeName: "Čeština", englishName: "Czech", status: "machine" },
  { code: "pl", nativeName: "Polski", englishName: "Polish", status: "machine" },
  { code: "ro", nativeName: "Română", englishName: "Romanian", status: "machine" },
  { code: "hr", nativeName: "Hrvatski", englishName: "Croatian", status: "machine" },
  { code: "sl", nativeName: "Slovenščina", englishName: "Slovenian", status: "machine" },
  { code: "sr-Latn", nativeName: "Srpski", englishName: "Serbian (Latin)", status: "machine" },
  { code: "uk", nativeName: "Українська", englishName: "Ukrainian", status: "machine" },
] as const;

export const LANGUAGE_CODES: readonly string[] = LANGUAGES.map((l) => l.code);

/** Picks the best supported language for a browser or OS language list. */
export function pickLanguage(preferred: readonly string[]): string {
  for (const p of preferred) {
    const exact = LANGUAGE_CODES.find((c) => c.toLowerCase() === p.toLowerCase());
    if (exact) return exact;
    const base = p.split("-")[0]!.toLowerCase();
    if (base === "sr") return "sr-Latn";
    const byBase = LANGUAGE_CODES.find((c) => c.split("-")[0]!.toLowerCase() === base);
    if (byBase) return byBase;
  }
  // Unknown language: English is understood more widely than the Hungarian source.
  return FALLBACK_FOR_UNKNOWN;
}
